import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash2,
  Pencil,
  MapIcon,
  Sparkles,
  Lock,
  Compass,
  Check,
  X,
} from "lucide-react";
import Button from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { cartomancerApi, mapFileUrl } from "./api";
import {
  CartomancerLoading,
  CartomancerAccessDenied,
  CartomancerHeader,
} from "./CartomancerShell";
import SplashScreen from "../../components/SplashScreen";

export default function ProjectsPage() {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: null,
    plan: "none",
    quota: 0,
    used: 0,
    projects: [],
    denied: false,
  });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await cartomancerApi.listProjects();
      setState({
        loading: false,
        error: null,
        plan: data.plan,
        quota: data.quota,
        used: data.used,
        projects: data.projects || [],
        denied: false,
      });
    } catch (err) {
      if (err.status === 403) {
        setState({
          loading: false,
          error: err.message,
          plan: "none",
          quota: 0,
          used: 0,
          projects: [],
          denied: true,
        });
      } else {
        setState((s) => ({ ...s, loading: false, error: err.message }));
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    load();
  }, [user, authLoading, navigate, load]);

  const handleCreate = async () => {
    const name = newName.trim() || t("carto.defaultProjectName");
    try {
      const proj = await cartomancerApi.createProject(name);
      setCreating(false);
      setNewName("");
      navigate(`/app/editor/${proj.id}`);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRename = async (id) => {
    if (!renameValue.trim()) {
      setRenameId(null);
      return;
    }
    try {
      await cartomancerApi.renameProject(id, renameValue.trim());
      setRenameId(null);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await cartomancerApi.deleteProject(id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (authLoading || state.loading) {
    return (
      <>
        <SplashScreen duration={3000} />
        <CartomancerLoading message={t("carto.loading")} />
      </>
    );
  }

  if (state.denied) {
    return (
      <>
        <SplashScreen duration={3000} />
        <CartomancerAccessDenied reason={state.error} />
      </>
    );
  }

  const { plan, quota, used, projects } = state;
  const emptySlots = Math.max(0, quota - projects.length);
  const canCreate = used < quota;

  return (
    <div className="min-h-screen flex flex-col" data-testid="projects-page">
      <SplashScreen duration={3000} />
      <CartomancerHeader
        title="Cartomancer"
        subtitle={t("carto.headerSubtitle")}
        plan={plan}
      />

      <main className="flex-1 px-4 md:px-6 py-10 max-w-6xl w-full mx-auto">
        {/* Intro */}
        <section className="mb-10">
          <p className="font-heading text-xs tracking-widest text-gold mb-3">
            {t("carto.archiveEyebrow")}
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-parchment mb-3">
            {t("carto.yourProjects")}
          </h2>
          <p className="font-serif-fantasy italic text-muted-foreground text-lg max-w-2xl">
            {t("carto.projectsQuote")}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 bg-card/50 font-heading text-xs tracking-widest uppercase text-gold"
              data-testid="quota-badge"
            >
              <Compass className="w-4 h-4" />
              {t("carto.projectsBadge", { used, quota })}
            </div>
            {plan === "basic" && (
              <Link to="/store" data-testid="upgrade-link">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-arcane/40 bg-arcane/10 font-heading text-xs tracking-widest uppercase text-arcane hover:border-arcane hover:bg-arcane/20 transition-all">
                  <Sparkles className="w-4 h-4" />
                  {t("carto.upgradePremium")}
                </span>
              </Link>
            )}
          </div>
        </section>

        {/* Projects grid */}
        <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              renaming={renameId === p.id}
              renameValue={renameValue}
              setRenameValue={setRenameValue}
              onStartRename={() => {
                setRenameId(p.id);
                setRenameValue(p.name);
              }}
              onCancelRename={() => setRenameId(null)}
              onSaveRename={() => handleRename(p.id)}
              onDelete={() => setConfirmDelete(p)}
              onOpen={() => navigate(`/app/editor/${p.id}`)}
            />
          ))}

          {/* Empty slots */}
          {Array.from({ length: emptySlots }).map((_, i) => {
            const isFirstEmpty = i === 0 && canCreate;
            return isFirstEmpty && creating ? (
              <CreateCard
                key={`create-${i}`}
                value={newName}
                onChange={setNewName}
                onCancel={() => {
                  setCreating(false);
                  setNewName("");
                }}
                onCreate={handleCreate}
              />
            ) : (
              <EmptySlot
                key={`empty-${i}`}
                disabled={!isFirstEmpty}
                onClick={() => isFirstEmpty && setCreating(true)}
                index={projects.length + i + 1}
              />
            );
          })}
        </section>

        {emptySlots === 0 && used === quota && (
          <div className="mt-8 rune-border bg-card/60 backdrop-blur rounded-lg p-6 text-center">
            <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
            <p className="font-heading text-sm text-parchment mb-3">
              {t("carto.quotaReached")}
            </p>
            {plan !== "premium" && (
              <Link to="/store">
                <Button variant="arcane" size="sm" data-testid="upgrade-cta">
                  {t("carto.upgradeCta", { count: 4 })}
                </Button>
              </Link>
            )}
          </div>
        )}
      </main>

      {/* Delete confirm */}
      {confirmDelete && (
        <ConfirmDialog
          title={t("carto.confirmDeleteTitle")}
          message={t("carto.confirmDeleteBody", { name: confirmDelete.name })}
          cancelLabel={t("carto.cancel")}
          confirmLabel={t("carto.delete")}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete.id)}
        />
      )}
    </div>
  );
}

function ProjectCardBase({
  project,
  renaming,
  renameValue,
  setRenameValue,
  onStartRename,
  onCancelRename,
  onSaveRename,
  onDelete,
  onOpen,
  t,
  locale,
}) {
  const thumb = project.map?.storage_path ? mapFileUrl(project.map.storage_path) : null;
  return (
    <div
      className="group rune-border bg-card/80 backdrop-blur rounded-lg overflow-hidden hover:arcane-glow transition-all flex flex-col"
      data-testid={`project-card-${project.id}`}
    >
      <div className="relative aspect-video bg-gradient-to-br from-secondary/30 to-background overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={project.name}
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
          />
        ) : (
          <div className="w-full h-full grid-overlay flex items-center justify-center">
            <MapIcon className="w-12 h-12 text-gold/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card/95 via-card/20 to-transparent pointer-events-none" />
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {renaming ? (
          <div className="flex items-center gap-1 mb-2">
            <input
              className="flex-1 bg-input border border-gold/40 rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-gold"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveRename();
                if (e.key === "Escape") onCancelRename();
              }}
              data-testid={`project-rename-input-${project.id}`}
            />
            <button
              onClick={onSaveRename}
              className="p-1 text-gold hover:bg-gold/10 rounded"
              data-testid={`project-rename-save-${project.id}`}
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={onCancelRename}
              className="p-1 text-muted-foreground hover:bg-border rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-heading text-lg text-gold leading-tight truncate">
              {project.name}
            </h3>
            <button
              onClick={onStartRename}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-gold"
              title={t("carto.rename")}
              data-testid={`project-rename-btn-${project.id}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-4">
          {project.map_count > 0 ? t("carto.mapLoaded") : t("carto.noMap")} ·{" "}
          {new Date(project.created_at).toLocaleDateString(locale)}
        </p>

        <div className="mt-auto flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={onOpen}
            data-testid={`project-open-btn-${project.id}`}
          >
            {project.map_count > 0 ? t("carto.openMap") : t("carto.startMapping")}
          </Button>
          <button
            onClick={onDelete}
            className="p-2 rounded border border-border hover:border-ruby/50 hover:text-ruby transition-all"
            title={t("carto.delete")}
            data-testid={`project-delete-btn-${project.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Thin wrapper that provides t() + locale so grandchildren stay pure.
function ProjectCard(props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language && i18n.language.startsWith("it") ? "it-IT" : "en-US";
  return <ProjectCardBase {...props} t={t} locale={locale} />;
}

function CreateCardBase({ value, onChange, onCancel, onCreate, t }) {
  return (
    <div className="rune-border bg-card/80 backdrop-blur rounded-lg p-6 flex flex-col gap-4 gold-glow" data-testid="create-project-form">
      <div>
        <h3 className="font-heading text-lg text-gold mb-1">{t("carto.newProject")}</h3>
        <p className="text-xs text-muted-foreground">{t("carto.nameYourWorld")}</p>
      </div>
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("carto.worldPlaceholder")}
        className="bg-input border border-gold/40 rounded px-3 py-2 text-sm text-foreground focus:outline-none focus:border-gold"
        onKeyDown={(e) => {
          if (e.key === "Enter") onCreate();
          if (e.key === "Escape") onCancel();
        }}
        data-testid="create-project-input"
      />
      <div className="flex gap-2">
        <Button variant="primary" size="sm" className="flex-1" onClick={onCreate} data-testid="create-project-submit">
          {t("carto.createProject")}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          {t("carto.cancel")}
        </Button>
      </div>
    </div>
  );
}

function CreateCard(props) {
  const { t } = useTranslation();
  return <CreateCardBase {...props} t={t} />;
}

function EmptySlotBase({ disabled, onClick, index, t }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`aspect-[4/3] rounded-lg border-2 border-dashed transition-all flex flex-col items-center justify-center gap-3 ${
        disabled
          ? "border-border/40 opacity-40 cursor-not-allowed"
          : "border-gold/40 hover:border-gold hover:bg-gold/5 cursor-pointer"
      }`}
      data-testid={disabled ? `empty-slot-locked-${index}` : "empty-slot-create"}
    >
      <div
        className={`w-14 h-14 rounded-full flex items-center justify-center ${
          disabled ? "bg-border/20" : "bg-gold/10"
        }`}
      >
        {disabled ? (
          <Lock className="w-6 h-6 text-muted-foreground" />
        ) : (
          <Plus className="w-6 h-6 text-gold" />
        )}
      </div>
      <span
        className={`font-heading text-xs tracking-widest uppercase ${
          disabled ? "text-muted-foreground" : "text-gold"
        }`}
      >
        {disabled ? t("carto.slot", { n: index }) : t("carto.newProject")}
      </span>
    </button>
  );
}

function EmptySlot(props) {
  const { t } = useTranslation();
  return <EmptySlotBase {...props} t={t} />;
}

function ConfirmDialog({ title, message, cancelLabel, confirmLabel, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-background/80 backdrop-blur-sm" data-testid="confirm-dialog">
      <div className="rune-border bg-card rounded-lg p-6 max-w-md w-full">
        <h3 className="font-heading text-xl text-ruby mb-3">{title}</h3>
        <p className="text-sm text-muted-foreground mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" size="sm" onClick={onCancel} data-testid="confirm-cancel">
            {cancelLabel}
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} data-testid="confirm-delete">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
